import { Dispatch, SetStateAction, useEffect } from "react"

import EventEmitter from "src/EventEmitter"

import {
  PromptClosedEventPayload,
  PromptOpenedEventPayload,
  PromptResponseEventPayload,
  PromptWithResponses,
} from "./types"

type SetPrompts = Dispatch<SetStateAction<PromptWithResponses[]>>

const usePromptEvents = (setPrompts: SetPrompts) => {
  useEffect(() => {
    const onPromptOpened = (data: PromptOpenedEventPayload) => {
      setPrompts(current => {
        if (current[0]?.id === data.promptId) return current

        return [
          {
            id: data.promptId,
            body: data.body,
            answerType: data.answerType,
            responses: [],
          },
          ...current,
        ]
      })
    }

    const onPromptClosed = (data: PromptClosedEventPayload) => {
      setPrompts(current => {
        const activePrompt = current[0]
        if (!activePrompt || activePrompt.id !== data.promptId) return current

        return [
          { ...activePrompt, closedAt: data.closedAt },
          ...current.slice(1),
        ]
      })
    }

    const onResponseSubmitted = (data: PromptResponseEventPayload) => {
      setPrompts(current => {
        const activePrompt = current[0]
        if (!activePrompt || activePrompt.id !== data.promptId) return current

        const alreadyAnswered = activePrompt.responses.some(
          response => response.student.id === data.studentId
        )
        if (alreadyAnswered) return current

        const newResponse = {
          id: data.responseId,
          student: {
            id: data.studentId,
            fullName: data.studentFullName,
          },
          answer: data.answer,
          answeredAt: data.answeredAt,
        }

        return [
          {
            ...activePrompt,
            responses: [...activePrompt.responses, newResponse],
          },
          ...current.slice(1),
        ]
      })
    }

    EventEmitter.subscribe("acr_class_prompt_opened", onPromptOpened)
    EventEmitter.subscribe("acr_class_prompt_closed", onPromptClosed)
    EventEmitter.subscribe(
      "acr_class_prompt_response_submitted",
      onResponseSubmitted
    )
    return () => {
      EventEmitter.unsubscribe("acr_class_prompt_opened", onPromptOpened)
      EventEmitter.unsubscribe("acr_class_prompt_closed", onPromptClosed)
      EventEmitter.unsubscribe(
        "acr_class_prompt_response_submitted",
        onResponseSubmitted
      )
    }
  }, [setPrompts])
}

export default usePromptEvents
